import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * useSearchBarUI - Refactorizado con separación de responsabilidades
 *
 * - Data Layer: formatea parámetros de búsqueda y ordenamiento
 * - UI Layer: determina iconos, tooltips y visibilidad de botones
 *
 * @param {function} onSearchChange - Handler del parent para cambios de búsqueda
 * @param {Object} sortConfig - Configuración de ordenamiento { field, direction }
 * @param {function} onSortChange - Handler del parent para cambios de ordenamiento
 * @param {function} onShowFiltersScreen - Handler del parent para toggle de filtros
 * @param {boolean} showFiltersScreen - Estado actual de pantalla de filtros
 * @returns {Object} Objeto con handlers y estado procesado para la presentación
 */
const useSearchBarUI = ({
  onSearchChange,
  sortConfig,
  onSortChange,
  onShowFiltersScreen,
  showFiltersScreen
}) => {
  const { t } = useTranslation('InboxMC');

  // --- Data Layer ---
  // Handler para cambios de búsqueda (solo formatea y delega)
  const handleSearchChange = useCallback((newSearchTerm) => {
    onSearchChange?.('searchTerm', newSearchTerm);
  }, [onSearchChange]);

  // Handler para cambio de ordenamiento (solo delega)
  const handleSortClick = useCallback(() => {
    if (onSortChange && sortConfig) {
      const newDirection = sortConfig.direction === 'asc' ? 'desc' : 'asc';
      onSortChange({ ...sortConfig, direction: newDirection });
    }
  }, [onSortChange, sortConfig]);

  // --- UI Layer ---
  // Icono de ordenamiento (memoizado)
  const sortIcon = useMemo(() => {
    if (!sortConfig || !sortConfig.direction) {
      return 'sort-desc-arrow-left';
    }
    return sortConfig.direction === 'asc' ? 'sort-asc-arrow-left' : 'sort-desc-arrow-left';
  }, [sortConfig]);

  // Título de ordenamiento (memoizado)
  const sortTitle = useMemo(() => {
    if (!sortConfig) {
      return t('search.sort_default');
    }
    return t('search.sort', {
      direction: t(`search.${sortConfig.direction}`)
    });
  }, [sortConfig, t]);

  // Título de filtros (memoizado)
  const filterTitle = useMemo(() => (
    showFiltersScreen ? t('search.close_filters') : t('search.filter')
  ), [showFiltersScreen, t]);

  // Visibilidad de botones (memoizado)
  const showSortButton = useMemo(() => !showFiltersScreen, [showFiltersScreen]);
  const showFilterButton = useMemo(() => !showFiltersScreen, [showFiltersScreen]);
  const showCloseButton = useMemo(() => showFiltersScreen, [showFiltersScreen]);

  // Handler para toggle de filtros (UI Layer, solo delega)
  const handleFiltersToggle = useCallback(() => {
    onShowFiltersScreen?.();
  }, [onShowFiltersScreen]);

  // Retorno memoizado para máxima estabilidad
  return useMemo(() => ({
    // Data Layer
    handleSearchChange,
    handleSortClick,

    // UI Layer
    handleFiltersToggle,
    sortIcon,
    sortTitle,
    filterTitle,
    showSortButton,
    showFilterButton,
    showCloseButton
  }), [
    handleSearchChange,
    handleSortClick,
    handleFiltersToggle,
    sortIcon,
    sortTitle,
    filterTitle,
    showSortButton,
    showFilterButton,
    showCloseButton
  ]);
};

export default useSearchBarUI;
